import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiTrashBold, PiUserPlusBold } from 'react-icons/pi';
import { Button, Input, Text } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import { invalidateDealCommissionQueries } from './invalidateDealCommissionQueries';

type SdrLinkState = {
    linked: boolean;
    sdr: { id: number; name: string; ploomes_id: number | null } | null;
    share_rate_pp: number | null;
    source: string | null;
};

type UserOption = {
    id: number;
    name: string;
    email?: string | null;
    ploomes_id?: number | null;
};

type Props = {
    open: boolean;
    onClose: () => void;
    dealId: number | string;
};

export default function DealSdrLinkModal({ open, onClose, dealId }: Props) {
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selected, setSelected] = useState<UserOption | null>(null);
    const [percent, setPercent] = useState('25');
    const [reason, setReason] = useState('');

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        if (!open) {
            setSearch('');
            setDebouncedSearch('');
            setSelected(null);
            setPercent('25');
            setReason('');
        }
    }, [open]);

    const linkQuery = useQuery({
        queryKey: ['commission-sdr-link', dealId],
        queryFn: async () => {
            const { data } = await api.get<{ data: SdrLinkState }>(
                `/commissioning/deals/${dealId}/sdr-link`,
            );
            return data.data;
        },
        enabled: open && Boolean(dealId),
        staleTime: 15_000,
    });

    useEffect(() => {
        const current = linkQuery.data;
        if (!current?.linked || !current.sdr) {
            setSelected(null);
            return;
        }
        setSelected({
            id: current.sdr.id,
            name: current.sdr.name,
            ploomes_id: current.sdr.ploomes_id,
        });
        if (current.share_rate_pp != null) {
            setPercent(String(current.share_rate_pp));
        }
    }, [linkQuery.data]);

    const usersQuery = useQuery({
        queryKey: ['users-search-sdr', debouncedSearch],
        queryFn: async () => {
            const { data } = await api.get<{ data: UserOption[] }>('/users', {
                params: {
                    search: debouncedSearch || undefined,
                    per_page: 20,
                },
            });
            return data.data ?? [];
        },
        enabled: open && debouncedSearch.length >= 2,
        staleTime: 30_000,
    });

    const invalidate = async () => {
        await invalidateDealCommissionQueries(queryClient, dealId);
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            if (!selected?.id) {
                throw new Error('missing_sdr');
            }
            const { data } = await api.put(`/commissioning/deals/${dealId}/sdr-link`, {
                sdr_user_id: selected.id,
                share_rate: Number(String(percent).replace(',', '.')),
                reason: reason.trim() || null,
            });
            return data;
        },
        onSuccess: async () => {
            toast.success(t('v2.ploomes_deals.sdr_link.toast_ok'));
            await invalidate();
            onClose();
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { errors?: Record<string, string[]> } } })
                    ?.response?.data?.errors?.sdr_user_id?.[0]
                ?? (err as { response?: { data?: { errors?: Record<string, string[]> } } })
                    ?.response?.data?.errors?.share_rate?.[0]
                ?? (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                ?? t('v2.ploomes_deals.sdr_link.toast_error');
            toast.error(msg);
        },
    });

    const removeMutation = useMutation({
        mutationFn: async () => {
            const { data } = await api.delete(`/commissioning/deals/${dealId}/sdr-link`, {
                data: { reason: reason.trim() || null },
            });
            return data;
        },
        onSuccess: async () => {
            toast.success(t('v2.ploomes_deals.sdr_link.toast_removed'));
            await invalidate();
            onClose();
        },
        onError: () => {
            toast.error(t('v2.ploomes_deals.sdr_link.toast_error'));
        },
    });

    const busy = saveMutation.isPending || removeMutation.isPending;
    const linked = Boolean(linkQuery.data?.linked);

    return (
        <V2Modal open={open} onClose={onClose} title={t('v2.ploomes_deals.sdr_link.title')} size="md">
            <div className="space-y-4">
                <Text className="text-sm text-gray-600 dark:text-zinc-400">
                    {t('v2.ploomes_deals.sdr_link.description')}
                </Text>

                {linked && linkQuery.data?.sdr && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-950 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-100">
                        {t('v2.ploomes_deals.sdr_link.current', {
                            name: linkQuery.data.sdr.name,
                            percent: String(linkQuery.data.share_rate_pp ?? '—'),
                        })}
                    </div>
                )}

                <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                        {t('v2.ploomes_deals.sdr_link.sdr')}
                    </label>
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('v2.ploomes_deals.sdr_link.search_placeholder')}
                    />
                    {selected && (
                        <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs dark:border-zinc-700 dark:bg-zinc-800/60">
                            <span className="font-medium text-gray-900 dark:text-zinc-100">{selected.name}</span>
                            <button
                                type="button"
                                className="text-gray-500 hover:text-gray-800 dark:hover:text-zinc-200"
                                onClick={() => setSelected(null)}
                            >
                                {t('v2.ploomes_deals.sdr_link.clear_selection')}
                            </button>
                        </div>
                    )}
                    {debouncedSearch.length >= 2 && (
                        <ul className="max-h-40 overflow-auto rounded-md border border-gray-200 dark:border-zinc-700">
                            {(usersQuery.data ?? []).map((user) => (
                                <li key={user.id}>
                                    <button
                                        type="button"
                                        className={cn(
                                            'flex w-full flex-col items-start px-2.5 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-zinc-800',
                                            selected?.id === user.id && 'bg-sky-50 dark:bg-sky-950/40',
                                        )}
                                        onClick={() => {
                                            setSelected(user);
                                            setSearch(user.name);
                                        }}
                                    >
                                        <span className="font-medium text-gray-900 dark:text-zinc-100">{user.name}</span>
                                        {user.email && (
                                            <span className="text-[10px] text-gray-500">{user.email}</span>
                                        )}
                                    </button>
                                </li>
                            ))}
                            {usersQuery.isFetched && (usersQuery.data ?? []).length === 0 && (
                                <li className="px-2.5 py-2 text-xs text-gray-500">
                                    {t('v2.ploomes_deals.sdr_link.no_results')}
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                        {t('v2.ploomes_deals.sdr_link.share_rate')}
                    </label>
                    <Input
                        value={percent}
                        onChange={(e) => setPercent(e.target.value)}
                        inputMode="decimal"
                    />
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                        {t('v2.ploomes_deals.sdr_link.reason')}
                    </label>
                    <Input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t('v2.ploomes_deals.sdr_link.reason_placeholder')}
                    />
                </div>

                <Text className="text-[11px] text-gray-500 dark:text-zinc-400">
                    {t('v2.ploomes_deals.sdr_link.hint')}
                </Text>

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 dark:border-zinc-800">
                    <div>
                        {linked && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 dark:border-red-800 dark:text-red-300"
                                isLoading={removeMutation.isPending}
                                disabled={busy}
                                onClick={() => removeMutation.mutate()}
                            >
                                <PiTrashBold className="mr-1 h-3.5 w-3.5" />
                                {t('v2.ploomes_deals.sdr_link.remove')}
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onClose}>
                            {t('v2.ploomes_deals.sdr_link.cancel')}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            isLoading={saveMutation.isPending}
                            disabled={busy || !selected?.id}
                            onClick={() => saveMutation.mutate()}
                        >
                            <PiUserPlusBold className="mr-1 h-3.5 w-3.5" />
                            {t('v2.ploomes_deals.sdr_link.save')}
                        </Button>
                    </div>
                </div>
            </div>
        </V2Modal>
    );
}
